import React from 'react';
import { Typography, Box, Button, Paper, Grid, Card, CardContent, Divider, Avatar, Stack } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
    Assessment, 
    Lock, 
    AttachMoney, 
    AdminPanelSettings, 
    Security, 
    VpnKey, 
    TrendingUp, 
    PeopleAlt, 
    Business 
} from '@mui/icons-material';

const OwnerDashboard = () => {
    const { user, logout } = useAuth();

    return (
        <Box sx={{ p: 4, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            {/* WELCOME BANNER HEAD COMPONENT */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 4, 
                    mb: 4, 
                    borderRadius: 4, 
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
                    color: '#fff',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                        <Avatar sx={{ width: 64, height: 64, bgcolor: '#3b82f6', fontSize: 24, fontWeight: 800 }}>
                            {user?.username?.charAt(0).toUpperCase()}
                        </Avatar>
                    </Grid>
                    <Grid item xs>
                        <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: '-0.5px' }}>
                            Owner Portal
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: '#94a3b8', mt: 0.5 }}>
                            Welcome back, <span style={{ color: '#f8fafc', fontWeight: 700 }}>{user?.username || 'Administrator'}</span>. Continuous operational system logs active.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* QUICK STATS COUNTERS */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>GLOBAL REACH</Typography>
                                <Typography variant="h5" fontWeight={800} color="#0f172a">All Branches</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: '#eff6ff', color: '#1d4ed8' }}><Business /></Avatar>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>SYSTEM HEALTH</Typography>
                                <Typography variant="h5" fontWeight={800} color="#16a34a">Operational</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: '#f0fdf4', color: '#16a34a' }}><TrendingUp /></Avatar>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>OVERSIGHT LEVEL</Typography>
                                <Typography variant="h5" fontWeight={800} color="#ea580c">Global Master</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: '#fff7ed', color: '#ea580c' }}><AdminPanelSettings /></Avatar>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>MANAGEMENT MODE</Typography>
                                <Typography variant="h5" fontWeight={800} color="#2563eb">Active Sync</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: '#f5f3ff', color: '#7c3aed' }}><PeopleAlt /></Avatar>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* DASHBOARD CORE ACTIONS CONTENT */}
            <Grid container spacing={4}>
                {/* GLOBAL CONTROL INTERFACE */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <AdminPanelSettings color="primary" />
                            <Typography variant="h5" fontWeight={800} color="#0f172a">Core Global Management</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Access macro system control panels, compile dynamic enterprise metrics, and execute operational validations.
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <Stack spacing={2}>
                            {/* FIXED PATH: Route maps directly to configured /hr/reports link */}
                            <Button 
                                variant="contained" 
                                color="primary" 
                                component={Link} 
                                to="/hr/reports" 
                                fullWidth
                                size="large"
                                startIcon={<Assessment />}
                                sx={{ borderRadius: 3, p: 1.8, fontWeight: 700 }}
                            >
                                VIEW GLOBAL REPORTS & ENTERPRISE METRICS
                            </Button>
                            
                            {/* FIXED PATH: Route maps directly to configured /hr/approvals link */}
                            <Button 
                                variant="contained" 
                                component={Link} 
                                to="/hr/approvals" FullWidth
                                size="large"
                                startIcon={<Lock />}
                                sx={{ borderRadius: 3, p: 1.8, fontWeight: 700, backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6d28d9' } }}
                            >
                                GLOBAL APPROVALS PORTAL (HR COMMAND)
                            </Button>
                            
                            {/* FIXED PATH: Route maps directly to configured /hr/payroll-docs link */}
                            <Button 
                                variant="contained" 
                                color="success" 
                                component={Link} 
                                to="/hr/payroll-docs" 
                                fullWidth
                                size="large"
                                startIcon={<AttachMoney />}
                                sx={{ borderRadius: 3, p: 1.8, fontWeight: 700 }}
                            >
                                GLOBAL PAYROLL MANAGEMENT & COMPENSATION LOGS
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
                
                {/* SECURITY COMMAND SECTION */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Security color="warning" />
                            <Typography variant="h5" fontWeight={800} color="#0f172a">Security & System Configuration</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Maintain systemic account protections and control administrative session states.
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                component={Link} 
                                to="/profile/change-password"
                                size="large"
                                startIcon={<VpnKey />}
                                sx={{ borderRadius: 3, p: 1.5, fontWeight: 700 }}
                            >
                                Update Security Password
                            </Button>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Button 
                            variant="contained" 
                            color="error" 
                            onClick={logout} 
                            size="large"
                            sx={{ borderRadius: 3, p: 1.5, fontWeight: 700, mt: 'auto' }}
                        >
                            Terminate Active System Session
                        </Button>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default OwnerDashboard;